import { useContext, useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { AuthContext, type AuthContextValue } from './authContextState'

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export function RequireAuth() {
  const { authenticated, configured, loading, refreshSession } = useAuth()
  const location = useLocation()
  const [slow, setSlow] = useState(false)

  useEffect(() => {
    if (!loading) {
      setSlow(false)
      return
    }
    const t = setTimeout(() => setSlow(true), 5000)
    return () => clearTimeout(t)
  }, [loading])

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-4 text-sm text-text-muted">
        <span>{slow ? 'Still loading…' : 'Loading…'}</span>
        {slow && (
          <button
            type="button"
            className="text-accent hover:underline"
            onClick={() => void refreshSession()}
          >
            Retry
          </button>
        )}
      </div>
    )
  }

  if (!configured) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <p className="max-w-md text-center text-sm text-text-secondary">
          Supabase is not configured. Set VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY,
          DATABASE_URL, SUPABASE_URL, and SUPABASE_ANON_KEY in your environment
          variables.
        </p>
      </div>
    )
  }

  if (!authenticated) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />
  }

  return <Outlet />
}

export function RequireSuperAdmin() {
  const { isSuperAdmin, loading } = useAuth()

  if (loading) {
    return (
      <div className="px-4 py-12 text-center text-sm text-text-muted">Loading…</div>
    )
  }

  if (!isSuperAdmin) {
    return <Navigate to="/status" replace />
  }

  return <Outlet />
}
